import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@material-ui/core/styles';
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc';
import { useDispatch, useSelector } from 'react-redux';
import './Portfolio.scss';
import CardIndicator from './CardIndicator/CardIndicator';
import AddIndicator from './AddIndicator/AddIndicator';
import { TabPanel } from '../Projects/ProjectsAll/ProjectAllUtil';
import { getRegionsAndCountries, getSectors } from '../../api/filterApi';
import { processIndicators } from './indicatorHelper';
import User from '../../models/User';
import FilterComponent from '../Filter/FilterComponent';
import { addRegions } from '../../redux/actions/regions';
import { CardPortfolioSkeletonList } from '../LoadersSkeletons/CardPortfolioSkeletonList/CardPortfolioSkeletonList';
import { translateMultipleText } from '../../services/stringUtil';
import { isIE11 } from '../../services/booleanUtil';
import { resetCurrentIndicator } from '../../redux/actions/currentIndicator';
import { resetIndicatorProjects } from '../../redux/actions/indicatorProjects';
import { sortIndicatorsList, addIndicators, getIndicatorsList } from '../../redux/actions/indicators';
import { resetIndicatorAccordances } from '../../redux/actions/indicatorAccordances';
import { emptyProjects, resetLocalStorageOnPortfolioMount } from './PortfolioUtil';
import { addSectors } from '../../redux/actions/sectors';
import { getCurrentDivisions } from '../../redux/actions/divisions';
import { CalledFrom } from '../../models/CalledFromEnum';
import { ProjectProductTypes } from '../../models/ProjectProductTypes';
import FilterIndicators from '../../models/FilterIndicators';
import Indicator from '../../models/Indicator';
import { resetSessionFilter } from '../../services/sessionFiltersIndicatorDetailHelper';
import { MenuTabsComponent } from '../MenuTabs/MenuTabsComponent';
import { getCurrentInterestIssues } from '../../redux/actions/interestIssues';
import { AppMenuTabs, MenuTabSectionEnum } from '../MenuTabs/userTabsInterface';
import { PortfolioTitle } from './PortfolioTitle/PortfolioTitle';
import CustomTabPanel from './CustomTabPanel/CustomTabPanel';
import EditCustomTabButton from '../../components/Util/Buttons/EditCustomTabButton/EditCustomTabButton';
import { getNewFilteredIndicators, getUserIndicatorCollection } from '../../api/indicatorApi';
import { updateCreatingOrEditing, updateMenuTabsFirstLoad } from '../../redux/actions/menuTabs';
import {
  resetIndicatorDetailFilter,
  resetIndicatorDetailFilterChart,
} from '../../redux/actions/indicatorDetailFilters';
import { trackActionClicked } from '../../api/trackActionClickedApi';
import { TrackedActions } from '../../models/TrackedActionsEnum';
import ModalProjectIntegration from '../PortfolioDetail/ModalProjectIntegration/ModalProjectIntegration';
import Filter from './Filter/Filter';
import PortfolioProductTable from './PortfolioProductTable/PortfolioProductTable';
import { useCards } from '../../context/CardContext';
import { setIndicatorResponse, setLoading } from '../../redux/actions/dataTable';
import FilterFocus from './FilterFocus/FilterFocus';
import { setUserIndicatorCollection } from '../../redux/actions/userIndicatorCollection';
import { OnePagerReportButton } from '../Util/Buttons/OnePagerReportButton/OnePagerReportButton';
import ErrorMessageView from '../Util/ErrorMessage/ErrorMessageView';
import { useDispatchNotificationsOnMount } from '../../hooks/useDispatchNotificationsOnMount';

const SortableItem = SortableElement(({ value, classSetter }) => (
  <CardIndicator card={value} classSetter={classSetter}></CardIndicator>
));
const SortableList = SortableContainer(({ items, classSetter }) => {
  const arrItems = items.filter(
    elemente =>
      elemente.indicatorKey !== 'PROJECTS_SUMMARY_PORTFOLIO' &&
      elemente.indicatorKey !== 'MONTHLY_DISBURSEMENT_PROJECTION_CURRENT_YEAR_INCLUDING_REGIONALS' &&
      elemente.indicatorKey !== 'PORTFOLIO_OVERVIEW_INCLUDING_REGIONAL_PROJECTS' &&
      elemente.indicatorKey !== 'DISBURSEMENT_PROJECTION_CURRENT_YEAR_INCLUDING_REGIONALS',
  );

  return (
    <div
      className={!classSetter ? 'sortable-container' : 'sortable-container focus-in'}
      style={isIE11() ? { width: '86vw' } : {}}
    >
      {arrItems.map((value, index) => (
        <SortableItem key={value.id} index={index} value={value} classSetter={classSetter} />
      ))}
      {!classSetter && <AddIndicator />}
    </div>
  );
});

export const getTabLabelText = (label, t) => {
  return label === 'global_bid' || label === 'my_portfolio' ? t(label) : label;
};

const emptySectors = sectors => sectors && !sectors.length;

const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

const obtainDataIfEmpty = (regions, sectors, divisions, interestIssues, dispatch) => {
  if (!regions.length) {
    getRegionsAndCountries().then(regionsData => {
      dispatch(addRegions(regionsData));
    });
  }

  if (emptySectors(sectors)) {
    getSectors().then(sectorsData => {
      dispatch(addSectors(sectorsData));
    });
  }

  if (!divisions.length) {
    dispatch(getCurrentDivisions());
  }

  if (!interestIssues.length) {
    dispatch(getCurrentInterestIssues());
  }
};
const addTrackTypeProjectPortfolio = (projectTypeFilter: ProjectProductTypes[]) => {
  const isTypeProjectPortfolioHomeTracked = localStorage.getItem('hasTrackProjectProductPortfolioHome');
  if (!isTypeProjectPortfolioHomeTracked) {
    const stringFiltersPortfolio = [...projectTypeFilter].sort((a: number, b: number) => a - b).join('|');
    trackActionClicked(TrackedActions.portfolioTypeFilterProjectChanged, stringFiltersPortfolio);
    localStorage.setItem('hasTrackProjectProductPortfolioHome', JSON.stringify(true));
  }
};
const Portfolio = () => {
  // FILTER FOCUS
  // ADD COMMENT FOR DEPLOYMENT
  const [filterFocusIndicators, setFilterFocusIndicators] = useState<any>(null);
  const [selectedText, setSelectedText] = useState('');
  const [value, setValue] = useState('');
  const [addFilterBorder, setAddFilterBorder] = useState<number | boolean | null>(null);

  useEffect(() => {
    setAddFilterBorder(selectedText.length);
  }, [selectedText]);

  const cssClassValidation = (addFilterBorder as number) > 0;

  const indicators = useSelector<any, Indicator[]>(state => state.indicators);

  const { setCards } = useCards();
  const dispatch = useDispatch();
  const summaryIndicator = indicators.filter(elemente => elemente.id === 15);

  useDispatchNotificationsOnMount();

  useEffect(() => {
    setCards(summaryIndicator[0]);
  }, [summaryIndicator, setCards]);

  const user = useSelector<any, User>(state => state.user);
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);

  //HOOKS
  const { t } = useTranslation();

  const theme = useTheme();

  //STATE
  const [totalProjects, setTotalProjects] = React.useState(false);
  const [filterIsOpen, setFilterIsOpen] = React.useState(false);
  const [customTabPanelIsOpen, setCustomTabPanelIsOpen] = React.useState(false);
  const [isCustomTabEdit, setIsCustomTabEdit] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const { divisions, sectors, regions, interestIssues } = useSelector<any, any>(state => state);
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [countError, setCountError] = useState(0)

  const isReduxLoading = useSelector<any>(state => state.dataTableInfo.indicatorLoading);

  const getCurrentUserIndicatorCollection = async () => {
    const data = await getUserIndicatorCollection();
    dispatch(setUserIndicatorCollection(data));
  };

  React.useEffect(() => {
    dispatch(resetCurrentIndicator());
    dispatch(getIndicatorsList());
    getCurrentUserIndicatorCollection();
    resetSessionFilter();
    resetLocalStorageOnPortfolioMount();
    dispatch(resetIndicatorDetailFilterChart());
    obtainDataIfEmpty(regions, sectors, divisions, interestIssues, dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    dispatch(resetCurrentIndicator());
    dispatch(resetIndicatorProjects());
    dispatch(resetIndicatorAccordances());
    addTrackTypeProjectPortfolio(filterIndicators.common.projectTypeFilters);
    setSelectedText('');

    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0 ||
      !menuTabs.portfolioSelectedTab.default
    )
      handleCommonFilterChange();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common, menuTabs.portfolioSelectedTab]);

  React.useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      tabId0OrCreatingEditing(menuTabs, creatingOrEditing) ||
      (!menuTabs.firstLoad &&
        filterIndicators.common.coReponsibleDivisions.length === 0 &&
        filterIndicators.common.countryId.length === 0 &&
        filterIndicators.common.divisionId.length === 0 &&
        filterIndicators.common.interestIssues.length === 0 &&
        filterIndicators.common.participantDivisions.length === 0 &&
        filterIndicators.common.regionId.length === 0 &&
        filterIndicators.common.sectors.length === 0 &&
        filterIndicators.common.teamLeaders.length === 0 &&
        menuTabs.portfolioSelectedTab.default)
    ) {
      handleCommonFilterChange();
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }
    if (menuTabs.firstLoad) {
      dispatch(updateMenuTabsFirstLoad(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuTabs, filterIndicators.common]);

  // FILTER FOCUS
  useEffect(() => {
    if (filterFocusIndicators) {
      const processedIndicators = processIndicators(filterFocusIndicators, {
        titleFromTab: 'Filtered Focus',
      });
      dispatch(addIndicators(processedIndicators));
    }
  }, [filterFocusIndicators, dispatch]);

  const onSortEnd = ({ oldIndex, newIndex }) => {
    dispatch(sortIndicatorsList(oldIndex, newIndex, t));
  };

  const closeModal = () => {
    setFilterIsOpen(false);
  };

  const RETRY_KEY = 'portfolio-following-error';
  const storageKey = `error-message-retries-${RETRY_KEY}`;

  const resetRetryCounter = () => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(storageKey);
  };

  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);
    setHasError(false);
    try {
      const indicatorsToProcess = await getNewFilteredIndicators();
      resetRetryCounter();
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
      setTotalProjects(emptyProjects(indicatorsToProcess.totalProjects));
      setHasError(false);
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
      // setHasError(true);
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    setIsLoading(false);
  };

  const handleCustomTabButtons = (fromAddButton: boolean) => {
    setIsCustomTabEdit(!fromAddButton);
    setCustomTabPanelIsOpen(true);
    trackActionClicked(TrackedActions.addCustomTabClicked);
  };

  return (
    <div className="indicators-container">
      <div className="top-header">
        <MenuTabsComponent
          selectedTab={portfolioSelectedTab}
          menuTabSection={MenuTabSectionEnum.PORTFOLIO_MENU_TAB}
          handleAddTabButton={() => handleCustomTabButtons(true)}
        />
      </div>
      <div className="header-title">
        <h1>
          <PortfolioTitle userLanguage={user.language.encodedName} selectedMenuTab={portfolioSelectedTab} />
        </h1>

        <div className={'filter-button'} id="filterComponent">

          <OnePagerReportButton isDisable={false} />
          <EditCustomTabButton
            handleButtonClick={() => handleCustomTabButtons(false)}
            selectedTab={portfolioSelectedTab}
          />
          <FilterComponent
            closeModal={closeModal}
            isOpen={filterIsOpen}
            indicatorCall={true}
            calledFrom={CalledFrom.PORTFOLIO}
          />
        </div>
      </div>
      <Filter calledFrom={CalledFrom.PORTFOLIO} />
      <CustomTabPanel
        isOpen={customTabPanelIsOpen}
        closePanel={() => setCustomTabPanelIsOpen(false)}
        isEdit={isCustomTabEdit}
        calledFrom={CalledFrom.CUSTOM_TABS_PORTFOLIO}
        selectedTab={portfolioSelectedTab}
      />

      {/* <FiltersErrorMessage /> */}

      {hasError ? (
        <div className="portfolio-error-message">
          <ErrorMessageView
            title={t('title_error_message_view')}
            description={t('desc_error_message_tab')}
            reload={handleCommonFilterChange}
            retryKey="portfolio-following-error"
            module={t('dashboard_portfolio')}
          />
        </div>
      ) : (
        <>
          <div className="portfolio-product-table">
            <PortfolioProductTable />
          </div>
          {isLoading || isReduxLoading ? (
            <CardPortfolioSkeletonList />
          ) : (
            <>
              <FilterFocus
                setIndicator={setFilterFocusIndicators}
                setLoading={setIsLoading}
                selectedText={selectedText}
                setSelectedText={setSelectedText}
                value={value}
                setValue={setValue}
              />
              <TabPanel
                key={portfolioSelectedTab.internalId}
                value={portfolioSelectedTab.internalId}
                index={portfolioSelectedTab.internalId}
                dir={theme.direction}
              >
                <SortableList
                  items={indicators}
                  onSortEnd={onSortEnd}
                  axis="xy"
                  pressDelay={200}
                  classSetter={cssClassValidation}
                />
              </TabPanel>
            </>
          )}
        </>
      )}
      <ModalProjectIntegration />
    </div>
  );
};

export default Portfolio;
